import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { ShieldCheck, Truck, RefreshCw, ArrowLeft, Check, Loader2, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import ProductStockAvailability from "@/components/ProductStockAvailability";
import ReserveDialog from "@/components/ReserveDialog";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const { format, currency, symbols } = useCurrency();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setP(data);
      } catch {
        setP(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const buyNow = async () => {
    if (!p) return;
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        product_id: p.id,
        quantity: 1,
        currency,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error(formatApiError(e));
      setBuying(false);
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-slate-500">Loading…</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">Product not found. <Link to="/shop" className="underline">Back to shop</Link></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="product-detail-view">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#1a2d5e]"><ArrowLeft size={14} /> Back to shop</Link>
      <div className="mt-6 grid lg:grid-cols-2 gap-12">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden aspect-square">
          {p.image_url ? (
            <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">No image</div>
          )}
        </div>
        <div>
          <div className="overline">{p.category} · {p.condition === "used" ? "second-hand" : "new"}</div>
          <h1 className="font-display font-black text-4xl mt-2 text-[#0b1838] tracking-tight">{p.title}</h1>
          <div className="mt-4 font-display font-black text-5xl text-[#1a2d5e]" data-testid="product-price">{format(p.price)}</div>
          <p className="mt-6 text-slate-600 leading-relaxed">{p.description || "Premium device backed by our 12-month warranty. Visit any branch for a hands-on demo, or order for delivery today."}</p>

          <div className="mt-8 flex gap-3">
            <button
              data-testid="product-buy-btn"
              disabled={buying || p.stock === 0}
              onClick={buyNow}
              className="flex-1 px-6 py-4 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {buying ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : <><Lock size={16} /> Buy now — {format(p.price)}</>}
            </button>
            <Link to="/contact" className="px-6 py-4 border border-slate-300 font-semibold rounded-lg hover:bg-slate-50">Ask a question</Link>
          </div>
          <div className="mt-2 text-xs text-slate-500">Secure checkout by Stripe · Cards, Apple Pay &amp; Google Pay accepted · You&apos;ll be charged in {symbols[currency]} {currency}</div>

          {p.sku && (
            <button
              type="button"
              onClick={() => setReserveOpen(true)}
              data-testid="product-reserve-btn"
              className="mt-4 w-full px-6 py-3.5 border-2 border-[#1a2d5e] text-[#1a2d5e] font-semibold rounded-lg hover:bg-[#1a2d5e] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
            >
              <ShoppingBag size={16} /> Reserve / Click &amp; Collect
            </button>
          )}

          {p.sku && (
            <div className="mt-6" data-testid="product-live-stock">
              <ProductStockAvailability sku={p.sku} />
            </div>
          )}

          <ul className="mt-8 space-y-2 text-sm text-slate-700">
            <li className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#1a2d5e]" /> 12-month JAS warranty included</li>
            <li className="flex items-center gap-2"><Truck size={16} className="text-[#1a2d5e]" /> Free UK delivery on orders over £200</li>
            <li className="flex items-center gap-2"><RefreshCw size={16} className="text-[#1a2d5e]" /> Trade-in available — get instant credit</li>
            <li className="flex items-center gap-2"><Check size={16} className="text-[#1a2d5e]" /> In stock: {p.stock} unit{p.stock === 1 ? "" : "s"}</li>
          </ul>
        </div>
      </div>
      {p.sku && (
        <ReserveDialog
          open={reserveOpen}
          onClose={() => setReserveOpen(false)}
          sku={p.sku}
          productTitle={p.title}
        />
      )}
    </div>
  );
}
